import { motion } from "motion/react";
import { EASE } from "@/components/motion/motion";
import { cn } from "@/lib/utils";

const VIEWPORT = { once: true, margin: "0px 0px -15% 0px" } as const;

/**
 * How a business application budget is spread over time, without amounts:
 * the initial build up front, running costs all along, improvements later.
 */
export function BudgetTimeline({
	items,
	className,
}: {
	items: { key: string; title: string; description: string }[];
	className?: string;
}) {
	const [initial, running, evolutions] = items;

	return (
		<div className={cn("rounded-3xl border bg-card p-6 md:p-10", className)}>
			<ol className="space-y-8">
				{[initial, running, evolutions].map((item, lane) =>
					item ? (
						<li
							key={item.key}
							className="grid gap-4 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.6fr)] md:items-center md:gap-10"
						>
							<div>
								<p className="font-mono text-xs text-signal-ink">
									{String(lane + 1).padStart(2, "0")}
								</p>
								<h3 className="mt-1 text-lg font-semibold tracking-tight">
									{item.title}
								</h3>
								<p className="mt-1 text-sm text-pretty text-muted-foreground">
									{item.description}
								</p>
							</div>
							<div
								aria-hidden="true"
								className="relative h-10 rounded-full bg-muted"
							>
								{lane === 0 ? (
									<motion.span
										className="absolute inset-y-1.5 left-1.5 w-[34%] origin-left rounded-full bg-foreground"
										initial={{ scaleX: 0 }}
										whileInView={{ scaleX: 1 }}
										viewport={VIEWPORT}
										transition={{ duration: 1, ease: EASE }}
									/>
								) : lane === 1 ? (
									<motion.span
										className="absolute inset-x-1.5 top-1/2 h-1.5 origin-left -translate-y-1/2 rounded-full bg-foreground/40"
										initial={{ scaleX: 0 }}
										whileInView={{ scaleX: 1 }}
										viewport={VIEWPORT}
										transition={{ duration: 1.6, ease: EASE, delay: 0.5 }}
									/>
								) : (
									[48, 64, 80, 93].map((left, dot) => (
										<motion.span
											key={left}
											className="absolute top-1/2 size-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-muted bg-signal"
											style={{ left: `${left}%` }}
											initial={{ opacity: 0, scale: 0.4 }}
											whileInView={{ opacity: 1, scale: 1 }}
											viewport={VIEWPORT}
											transition={{
												duration: 0.4,
												ease: EASE,
												delay: 1.2 + dot * 0.18,
											}}
										/>
									))
								)}
							</div>
						</li>
					) : null,
				)}
			</ol>
		</div>
	);
}
